import type { HitProps } from '@components/algolia/Hit';

/**
 * The colour key for an authored variant SKU. `variants` is keyed by colour, so
 * the SKU has to be looked up before `useHitSwatches` runs — the hook selects by
 * colour, not by SKU. A size-level SKU usually extends its colourway's SKU, so
 * it resolves to the colourway with the longest matching prefix; one that
 * doesn't resolves through `variantSkuColours` (see `attachVariantSkuColours`).
 * Falls back to the record's own colour when the SKU is absent or unknown, so a
 * stale SKU degrades to the default colourway rather than dropping the
 * card/hotspot.
 */
export const colourKeyForVariantSku = (
  record: HitProps,
  variantSku: string | null | undefined
): string => {
  const sku = variantSku?.trim();

  if (!sku) {
    return record.colour;
  }

  const [match] = Object.entries(record.variants ?? {})
    .filter(([, variant]) => variant?.sku && sku.startsWith(variant.sku))
    .sort(([, a], [, b]) => b.sku.length - a.sku.length);

  return match?.[0] ?? record.variantSkuColours?.[sku] ?? record.colour;
};

export default colourKeyForVariantSku;
